import type {
  ReportGridFilterModel,
  ReportPreviewColumn,
  ResolvedReportPlan,
  ResolvedReportPlanInput,
  ToolApprovalResponse,
} from '@aline/common';

/** The card's edit state over the proposed plan. */
export interface ReportPlanEdits {
  /** Extraction columns the user took off the plan, by position. */
  excludedIndices: Set<number>;
  filterModel: ReportGridFilterModel;
  plan: ResolvedReportPlan;
}

export function toggleExcludedIndex(
  columns: readonly ReportPreviewColumn[],
  excluded: ReadonlySet<number>,
  index: number
) {
  const next = new Set(excluded);
  const related = collectTransitiveColumnIndices({
    columns,
    followInputs: next.has(index),
    startIndex: index,
  });
  if (next.has(index)) {
    for (const relatedIndex of related) next.delete(relatedIndex);
  } else {
    for (const relatedIndex of related) next.add(relatedIndex);
  }
  return next;
}

/**
 * Walks from a column along the plan graph. `followInputs` goes to columns
 * this one reads; otherwise it goes to columns that read this one.
 */
function collectTransitiveColumnIndices({
  columns,
  followInputs,
  startIndex,
}: {
  columns: readonly ReportPreviewColumn[];
  followInputs: boolean;
  startIndex: number;
}) {
  const queue = [startIndex];
  const indices = new Set<number>([startIndex]);
  const indexByKey = new Map(columns.map((column, columnIndex) => [column.key, columnIndex]));

  while (queue.length > 0) {
    const currentIndex = queue.shift();
    const column = currentIndex === undefined ? undefined : columns[currentIndex];
    if (column === undefined) break;

    if (followInputs) {
      for (const key of column.inputColumnKeys) {
        const parentIndex = indexByKey.get(key);
        if (parentIndex !== undefined && !indices.has(parentIndex)) {
          indices.add(parentIndex);
          queue.push(parentIndex);
        }
      }
    } else {
      columns.forEach((other, otherIndex) => {
        if (!indices.has(otherIndex) && other.inputColumnKeys.includes(column.key)) {
          indices.add(otherIndex);
          queue.push(otherIndex);
        }
      });
    }
  }

  return indices;
}

export function getInputColumnTitles(
  column: ReportPreviewColumn,
  columns: readonly ReportPreviewColumn[]
) {
  const titleByKey = new Map(columns.map(planColumn => [planColumn.key, planColumn.title]));
  return column.inputColumnKeys.flatMap(key => {
    const title = titleByKey.get(key);
    return title ? [title] : [];
  });
}

export function stripDanglingInputColumnKeys(
  columns: ReportPreviewColumn[]
): ReportPreviewColumn[] {
  const keys = new Set(columns.map(column => column.key));
  return columns.map(column => ({
    ...column,
    inputColumnKeys: column.inputColumnKeys.filter(key => keys.has(key)),
  }));
}

/**
 * The response the card sends for the user's decision, which is only ever
 * this plan or no plan. A skip carries nothing. An approval carries the plan
 * with the card's edits applied, which is what the tool creates. Asking for a
 * different plan is a chat message, not a card action: sending one supersedes
 * the pending approval and starts a new turn.
 */
export function buildReportPlanApprovalResponse({
  action,
  approvalId,
  edits,
  messageId,
}: {
  action: 'approve' | 'skip';
  approvalId: string;
  edits: ReportPlanEdits;
  messageId: string;
}): ToolApprovalResponse {
  if (action === 'skip') return { approved: false, id: approvalId, messageId };
  return { approved: true, id: approvalId, input: buildApprovedReportPlan(edits), messageId };
}

/**
 * The plan the tool executes once the user approves: the proposal with the
 * card's edits applied. Excluded extraction columns are omitted. Built-in
 * columns remain unchanged because the report contract always includes its
 * default set.
 */
export function buildApprovedReportPlan({
  excludedIndices,
  filterModel,
  plan,
}: ReportPlanEdits): ResolvedReportPlanInput {
  return {
    ...plan,
    columns: stripDanglingInputColumnKeys(
      plan.columns.filter((_, index) => !excludedIndices.has(index))
    ),
    filterModel,
  };
}
